import {
  getGridStringOperators,
  GridColDef,
  GridFilterOperator,
  gridStringOrNumberComparator,
  GetApplyFilterFn,
} from '@mui/x-data-grid-pro';
import { useLanguageCode } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { I18nCell, I18nEditCell } from '../../cells';
import { CmdiColumnVariant, CmdiColumnVariantType } from '~/common/types/CmdiColumnVariant';
import { cellFactory, DataGridConfig, editCellFactory } from '~/components/DataGridCustom';

import { CmdiColumnConfig, CmdiRow, CmdiRowFactory } from './CmdiDataGridWidget.types';

type CreateColumnVariantProps = GridColDef & {
  variant: CmdiColumnVariant;
  options: Record<string, any>;
  languageCode: string;
};

const createColumnVariant = ({
  variant,
  languageCode,
  options,
  ...colDef
}: CreateColumnVariantProps): GridColDef | null => {
  const wrapOperator = (operator: GridFilterOperator) => {
    const getApplyFilterFn: GetApplyFilterFn = (filterItem, column) => {
      const innerFilterFn = operator.getApplyFilterFn(filterItem, column);
      if (!innerFilterFn) {
        return innerFilterFn;
      }
      const result: NonNullable<ReturnType<GetApplyFilterFn>> = (value, row, column, apiRef) => {
        return innerFilterFn(value?.[languageCode], row, column, apiRef);
      };
      return result;
    };

    return {
      ...operator,
      getApplyFilterFn,
    };
  };
  switch (variant) {
    case CmdiColumnVariantType.I18N:
      return {
        ...colDef,
        type: 'string',
        renderCell: cellFactory(I18nCell, options),
        renderEditCell: editCellFactory(I18nEditCell, options),
        filterOperators: getGridStringOperators().map((operator) => wrapOperator(operator)),
        sortComparator: (v1, v2, cellParams1, cellParams2) => {
          return gridStringOrNumberComparator(v1?.[languageCode], v2?.[languageCode], cellParams1, cellParams2);
        },
      };
    case CmdiColumnVariantType.NUMBER:
      return { ...colDef };
    case CmdiColumnVariantType.STRING:
      return { ...colDef };
  }
  return null;
};

const variantDefaultValueMap: { [key in CmdiColumnVariant]: any } = {
  [CmdiColumnVariantType.I18N]: { DE: '', EN: '' },
  [CmdiColumnVariantType.STRING]: '',
  [CmdiColumnVariantType.NUMBER]: 0,
};

export type UseCmdiDataGridConfigResult = {
  config: DataGridConfig<CmdiRow>;
  rowFactory: CmdiRowFactory;
};

export type UseCmdiDataGridConfig = (columnConfig: CmdiColumnConfig[]) => UseCmdiDataGridConfigResult;

export const useCmdiDataGridConfig: UseCmdiDataGridConfig = (columnConfig) => {
  const { languageCode } = useLanguageCode();
  const languageCodeUpper = languageCode.toUpperCase();

  return useMemo(() => {
    return {
      config: {
        columns: columnConfig.map((c) => {
          return createColumnVariant({
            variant: c.variant as CmdiColumnVariant,
            options: c.options,
            languageCode: languageCodeUpper,
            field: c.columnName,
            headerName: c.i18n[languageCodeUpper],
            width: 300,
            minWidth: 300,
            editable: true,
            pinnable: false,
          });
        }),
      },
      rowFactory: () => {
        return columnConfig.reduce(
          (acc, col) => {
            acc[col.columnName] = variantDefaultValueMap[col.variant as CmdiColumnVariant];
            return acc;
          },
          { id: uuidv4() } as CmdiRow,
        );
      },
    } as UseCmdiDataGridConfigResult;
  }, [columnConfig, languageCodeUpper]);
};
